import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import useReveal from "../hooks/useReveal";
import { api } from "../lib/api";
import Seo from "../components/Seo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/1f9c6200-ca73-4c9b-a307-361cf054655d/images/3fc7d61832bc43c487502059fecc1c8216bc76a0a518df79668d5d328ae3cfc8.png";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/1f9c6200-ca73-4c9b-a307-361cf054655d/images/4b45544fb2792344fcd310d09e88872a4281e4d33367f209810db61c9bae9299.png";
const DUBAI = "https://images.pexels.com/photos/27811085/pexels-photo-27811085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LONDON = "https://images.pexels.com/photos/8461520/pexels-photo-8461520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const specialisms = [
  { num: "01", title: "Quantity Surveying", desc: "Cost certainty across the project lifecycle." },
  { num: "02", title: "Commercial, Contracts & Claims", desc: "Pre and post-contract leadership across FIDIC, NEC and JCT forms." },
  { num: "03", title: "Dispute Resolution", desc: "Quantum, Delay and Technical Experts for complex construction disputes." },
  { num: "04", title: "Planning", desc: "Programme integrity from tender through to handover." },
  { num: "05", title: "Programme & Project Management", desc: "Delivery leadership on major construction and infrastructure schemes." },
];

export default function Home() {
  useReveal();
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api.get("/insights").then((r) => setInsights(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo title={null} description="B7 Search & Selection — specialist construction and infrastructure recruitment across the UK, Europe and the Middle East." path="/" />

      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1530]/95 via-[#0E1530]/55 to-[#0E1530]/35" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pb-16 pt-32 w-full">
          <div className="overline-light mb-4" data-testid="hero-overline">
            B7 Search & Selection — UK • Europe • Middle East
          </div>
          <h1 className="font-serif text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl" data-testid="hero-headline">
            Business driven.{" "}<br />
            <span className="italic font-light text-[#9DC8F2]">People focused.</span>
          </h1>
          <p className="text-white/85 text-base sm:text-lg max-w-xl mt-8 leading-relaxed" data-testid="hero-subtitle">
            Specialist recruitment for the global construction sector — connecting commercial,
            planning and dispute resolution talent with the world's most ambitious construction
            and infrastructure projects.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/contact" className="btn-primary" data-testid="hero-contact-btn">
              Contact us <ArrowRight size={16} />
            </Link>
            <Link to="/vacancies" className="btn-ghost-light" data-testid="hero-vacancies-btn">
              View vacancies
            </Link>
          </div>
        </div>
      </section>

      {/* TRUSTED MARQUEE */}
      <section className="py-10 border-y border-[#E2E0D9] overflow-hidden bg-[#F4F3F0]" data-testid="marquee-section">
        <div className="marquee-track flex gap-16 whitespace-nowrap font-serif text-3xl sm:text-4xl text-[#5C5C5C]">
          {[
            "HS2", "Diriyah Gate",
            "Crossrail", "Wynn Hotel — Ras Al Khaimah",
            "Lower Thames Crossing", "NEOM",
            "Thames Tideway", "Palm Jebel Ali",
            "Sizewell C", "Etihad Rail",
            "Hinkley Point C", "Riyadh Metro",
            "Battersea Power Station", "Museum of the Future",
            "Heathrow", "Expo City Dubai",
            "HS2", "Diriyah Gate",
            "Crossrail", "Wynn Hotel — Ras Al Khaimah",
            "Lower Thames Crossing", "NEOM",
            "Thames Tideway", "Palm Jebel Ali",
            "Sizewell C", "Etihad Rail",
            "Hinkley Point C", "Riyadh Metro",
            "Battersea Power Station", "Museum of the Future",
            "Heathrow", "Expo City Dubai",
          ].map((p, i) => (
            <span key={i} className="flex items-center gap-16">
              {p}
              <span className="text-[#2EA3F2]">◆</span>
            </span>
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20" data-testid="about-teaser-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <div className="overline">Bespoke Solutions</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-6">
              18 years pairing<br />talent with the world's most<br />
              <span className="italic text-[#1E2A78]">ambitious</span> projects.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 reveal">
            <p className="text-lg leading-relaxed text-[#222]">
              A resume tells you what someone has done. Our job is to understand who they are —
              and to match that with the precise needs of employers building across the UK,
              Europe and the Middle East.
            </p>
            <p className="text-lg leading-relaxed text-[#222] mt-6">
              Clients receive side-by-side comparisons of the most relevant candidates, with
              objective guidance from a team of qualified construction specialists.
            </p>
            <Link to="/about" className="inline-flex items-center gap-3 mt-10 link-underline text-sm uppercase tracking-[0.22em] font-semibold text-[#1E2A78]" data-testid="home-about-link">
              About B7 <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SPECIALISMS */}
      <section className="bg-[#FFFFFF] border-y border-[#E2E0D9]" data-testid="specialisms-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10">
            <div className="lg:col-span-4">
              <div className="overline">Specialisms</div>
              <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-6">
                Five disciplines.<br />One standard.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-base text-[#5C5C5C] leading-relaxed">
                Senior search across the commercial spine of construction — from cost certainty
                through to expert evidence in formal dispute proceedings.
              </p>
            </div>
          </div>

          <div className="border-t border-[#E2E0D9]">
            {specialisms.map((s) => (
              <Link
                key={s.num}
                to="/services"
                className="spec-row group flex items-center justify-between border-b border-[#E2E0D9] py-8 sm:py-10 cursor-pointer"
                data-testid={`specialism-row-${s.num}`}
              >
                <div className="flex items-baseline gap-8 sm:gap-12 flex-1">
                  <span className="overline w-10">{s.num}</span>
                  <div>
                    <h3 className="font-serif text-2xl sm:text-4xl tracking-tight">{s.title}</h3>
                    <p className="text-sm text-[#5C5C5C] mt-2 max-w-xl">{s.desc}</p>
                  </div>
                </div>
                <ArrowUpRight size={28} className="spec-arrow text-[#0E1530]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20" data-testid="regions-section">
        <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-8">
          Regions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { img: LONDON, name: "United Kingdom & Europe", sub: "London · Manchester · Dublin · Frankfurt" },
            { img: DUBAI, name: "Middle East", sub: "Dubai (HQ) · Riyadh · Doha · Abu Dhabi" },
          ].map((r) => (
            <div
              key={r.name}
              className="relative group h-[440px] sm:h-[520px] overflow-hidden cursor-pointer"
              data-testid={`region-card-${r.name.split(' ')[0].toLowerCase()}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${r.img})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E1530]/95 via-[#0E1530]/35 to-transparent" />
              <div className="absolute inset-0 p-8 sm:p-10 flex flex-col justify-end">
                <div className="overline-light">Region</div>
                <h3 className="font-serif text-3xl sm:text-4xl text-white mt-3">{r.name}</h3>
                <p className="text-sm text-white/80 mt-2">{r.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA SPLIT */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${TEXTURE})` }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#0E1530]/92" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="text-white">
            <div className="overline-light">Hiring</div>
            <h3 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Build the team that builds the project.</h3>
            <p className="text-white/70 mt-6 max-w-md">
              Share your hiring need — a B7 consultant will be in touch to scope the search.
            </p>
            <Link to="/contact" className="btn-ghost-light mt-8" data-testid="cta-contact-btn">
              Contact us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="text-white md:border-l border-white/15 md:pl-12">
            <div className="overline-light">Job Seekers</div>
            <h3 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">The next move, made deliberately.</h3>
            <p className="text-white/70 mt-6 max-w-md">
              Browse live vacancies or share a confidential CV with a consultant who understands
              your discipline.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/vacancies" className="btn-ghost-light" data-testid="cta-vacancies-btn">
                Vacancies
              </Link>
              <Link to="/submit-cv" className="btn-ghost-light" data-testid="cta-submit-cv">
                Submit CV
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20" data-testid="insights-section">
        <div className="flex items-end justify-between gap-8 mb-10 flex-wrap">
          <div>
            <div className="overline">Market Insights</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-6">
              Reading the market.
            </h2>
          </div>
          <Link to="/insights" className="link-underline text-sm uppercase tracking-[0.22em] font-semibold text-[#1E2A78]" data-testid="home-insights-all-link">
            All insights →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {insights.slice(0, 3).map((p) => (
            <Link
              key={p.id}
              to={`/insights/${p.slug}`}
              className="group block"
              data-testid={`insight-card-${p.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#E2E0D9]">
                <img
                  src={p.image_url}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="mt-6">
                <div className="overline">{p.category}</div>
                <h3 className="font-serif text-2xl mt-3 leading-tight group-hover:text-[#1E2A78] transition-colors">
                  {p.title}
                </h3>
                <p className="text-xs text-[#5C5C5C] mt-3">
                  {new Date(p.published_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
